/**
 * Production CSP is `script-src 'self' 'nonce-…'`. A nonce makes the browser
 * ignore `'unsafe-inline'`, so any script Unhead creates after hydration is
 * blocked unless it carries that same nonce.
 *
 * Unhead does this in `unhead/dist/client.mjs` (`renderDOMHead`): it builds a
 * fragment and appends bodyClose tags at the line the console reports. Chrome
 * does not honor a nonce on a script that arrives through that fragment.
 * Pull the script out and insert it on the document with `.nonce` set first.
 * SSR HTML is already stamped in `server/plugins/csp.ts`.
 */
export default defineNuxtPlugin({
  name: 'csp-nonce',
  enforce: 'pre',
  setup (nuxtApp) {
    const fromMeta = document.querySelector('meta[name="csp-nonce"]')?.getAttribute('content')?.trim() || ''
    const fromScript = document.querySelector('script[nonce]')?.nonce || ''
    const nonce = fromMeta || fromScript
    if (!nonce) return

    const head = injectHead(nuxtApp)
    if (!head?.hooks?.hook) return

    head.hooks.hook('tags:resolve', (ctx: { tags?: Array<{ tag?: string, props?: Record<string, unknown> }> }) => {
      for (const tag of ctx.tags || []) {
        if (tag.tag !== 'script') continue
        const props = (tag.props ||= {})
        if (props.src) continue
        if (!props.nonce) props.nonce = nonce
      }
    })

    head.hooks.hook('dom:renderTag', (ctx: { $el?: Element, tag?: { tagPosition?: string } }) => {
      const el = ctx.$el
      if (!(el instanceof HTMLScriptElement) || el.src) return
      // Property, not only the attribute: CSP reads `.nonce` when the element
      // was created by script rather than the parser.
      el.nonce = nonce
      if (el.getAttribute('nonce') !== nonce) el.setAttribute('nonce', nonce)

      const parent = el.parentNode
      if (parent?.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) return
      parent.removeChild(el)
      const pos = ctx.tag?.tagPosition || 'head'
      if (pos === 'bodyClose') document.body.appendChild(el)
      else if (pos === 'bodyOpen') document.body.insertBefore(el, document.body.firstChild)
      else document.head.appendChild(el)
    })
  },
})
